import UserStore from "../store";
import { ECSConfigService, ECSConfigKey } from "sh-services";

/**
 * Determines if a feedback panel should be shown based on timing rules.
 *
 * Logic:
 * - First time (never shown): Always show
 * - After submit: Show only if submitPeriodInDays have passed since last submitted
 * - After dismiss: Show only if dismissPeriodInDays have passed since last dismissed
 * - Uses the most recent timestamp between submit and dismiss to determine display
 *
 * @param scenario - Scenario identifier (e.g., "autoScheduleApprove", "autoScheduleReject")
 * @param submitECSKey - ECS config key for submit period
 * @param dismissECSKey - ECS config key for dismiss period
 * @returns true if feedback should be shown, false otherwise
 */
export function shouldShowFeedback(scenario: string, submitECSKey: ECSConfigKey, dismissECSKey: ECSConfigKey): boolean {
    // If we could not retrieve user settings, do not show feedback
    if (!UserStore().userSettings) {
        return false;
    }

    // Get feedback timestamps from backend
    const feedbackTimestamps = UserStore().userSettings.feedbackTimestamps;

    // If backend doesn't have feedbackTimestamps field yet, treat as first time
    // The field will be initialized when we save the timestamp
    if (!feedbackTimestamps) {
        return true;
    }

    // Get the scenario-specific timestamps
    const scenarioTimestamps = feedbackTimestamps[scenario];

    // If this scenario has never been tracked, treat as first time
    if (!scenarioTimestamps) {
        return true;
    }

    // Get both submit and dismiss timestamps
    const lastSubmitTimestamp = scenarioTimestamps.submit;
    const lastDismissTimestamp = scenarioTimestamps.dismiss;

    // First time - neither submit nor dismiss has happened
    if (!lastSubmitTimestamp && !lastDismissTimestamp) {
        return true;
    }

    const now = Date.now();

    // Get ECS config values for periods
    const submitPeriodInDays = (ECSConfigService.getECSFeatureSetting(submitECSKey) as number) || 7;
    const dismissPeriodInDays = (ECSConfigService.getECSFeatureSetting(dismissECSKey) as number) || 1;

    // Determine which action happened most recently (submit or dismiss)
    let mostRecentAction: "submit" | "dismiss" | null = null;
    let mostRecentTimestamp = 0;

    if (lastSubmitTimestamp && lastDismissTimestamp) {
        // Both exist - use the most recent one
        if (lastSubmitTimestamp > lastDismissTimestamp) {
            mostRecentAction = "submit";
            mostRecentTimestamp = lastSubmitTimestamp;
        } else {
            mostRecentAction = "dismiss";
            mostRecentTimestamp = lastDismissTimestamp;
        }
    } else if (lastSubmitTimestamp) {
        // Only submit exists
        mostRecentAction = "submit";
        mostRecentTimestamp = lastSubmitTimestamp;
    } else if (lastDismissTimestamp) {
        // Only dismiss exists
        mostRecentAction = "dismiss";
        mostRecentTimestamp = lastDismissTimestamp;
    }

    // Check if enough time has passed since the most recent action
    const daysSinceMostRecent = (now - mostRecentTimestamp) / (1000 * 60 * 60 * 24);
    const requiredPeriod = mostRecentAction === "submit" ? submitPeriodInDays : dismissPeriodInDays;
    const canShow = daysSinceMostRecent >= requiredPeriod;

    return canShow;
}
